import React, { useCallback, useContext, useRef, useState } from "react";
import { Upload, FileText, Loader } from "lucide-react";
import styles from "./appendix.module.css";
import { AppendixContext } from "../../../../../../../../contexts/AppendixContext";
import { AppendixFile } from "../../../../../../../../sdkStructs";
import { AuthContext } from "../../../../../../../../contexts/AuthContext";
import {
  apiPostAppendixFile,
  apiDeleteAppendixFile,
} from "../../../../../../../../sdk";
import { TemplateContext } from "../../../../../../../../contexts/TemplateContext";

const Appendix = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { token } = useContext(AuthContext);
  const { template } = useContext(TemplateContext);
  const { appendix, setAppendix } = useContext(AppendixContext);

  const handleFile = useCallback(
    async (file: File) => {
      if (!template?.ID || !token) {
        setUploadError("Please select a template first.");
        return;
      }
      
      setIsUploading(true);
      setUploadError(null);
      
      try {
        const response = await apiPostAppendixFile(token, template.ID, file);
        if (response.data.appendixID) {
          setAppendix((prevAppendix: AppendixFile[]) => {
            return [...prevAppendix, {
                ID: Number(response.data.appendixID),
                Template: template.ID,
                OriginalName: file.name,
              },
            ];
          });
        }
      } catch (error) {
        console.error("Failed to upload appendix file:", error);
        setUploadError("Failed to upload PDF. Please try again.");
      } finally {
        setIsUploading(false);
      }
    },
    [token, template?.ID, setAppendix],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const file = e.dataTransfer.files?.[0];
      if (file && file.type === "application/pdf") {
        handleFile(file);
      } else if (file) {
        setUploadError("Only PDF files are allowed.");
      }
    },
    [handleFile],
  );

  const handleRemove = useCallback(
    async (id: number) => {
      if (!token) return;
      
      setIsDeleting(id);
      try {
        await apiDeleteAppendixFile(token, id);
        setAppendix((prevAppendix: AppendixFile[]) => {
          const updatedAppendixFiles = prevAppendix.filter(
            (file: AppendixFile) => file.ID !== id
          );
          return updatedAppendixFiles;
        });
      } catch (error) {
        console.error("Failed to delete appendix file:", error);
      } finally {
        setIsDeleting(null);
      }
    },
    [setAppendix, token],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FileText className={styles.icon} />
        <h1>Appendix Files</h1>
      </div>
      <div
        className={`${styles.uploadSection} ${isDragging ? styles.dragging : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <div className={styles.previewContainer}>
            <div className={styles.placeholder}>
              <Loader className={`${styles.placeholderIcon} ${styles.spinner}`} />
              <p className={styles.placeholderText}>Uploading PDF...</p>
            </div>
          </div>
        ) : (
          <div className={styles.previewContainer}>
            <div className={styles.placeholder}>
              <FileText className={styles.placeholderIcon} />
              <p className={styles.placeholderText}>
                {isDragging 
                  ? "Drop your file here" 
                  : template?.ID 
                    ? "Drag & drop your PDF file here" 
                    : "Select template to enable PDF upload"}
              </p>
              {uploadError && <p className={styles.errorText}>{uploadError}</p>}
            </div>
          </div>
        )}
        <div className={styles.actions}>
          <button 
            className={styles.uploadButton} 
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || !template?.ID}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".pdf"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file && file.type === "application/pdf") {
                  await handleFile(file);
                } else if (file) {
                  setUploadError("Only PDF files are allowed.");
                }
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            />
            <Upload className={styles.buttonIcon} />
            {isUploading ? "Uploading..." : "Upload PDF"}
          </button>
        </div>
      </div>
      <ul className={styles.pdfList}>
        {appendix?.map((pdf: AppendixFile, index: number) => (
          <li key={index} className={styles.pdfItem}>
            <span>{pdf.OriginalName}</span>
            <button 
              className={styles.removeButton} 
              onClick={() => handleRemove(pdf.ID)}
              disabled={isDeleting === pdf.ID || isUploading}
            >
              {isDeleting === pdf.ID ? "Removing..." : "Remove"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Appendix;